import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-72px)] items-center justify-center bg-brand-ink px-5 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="inline-flex bg-white p-3">
            <img src="/logo.webp" alt="MATRIVON" width="480" height="480" decoding="async" className="h-20 w-auto" />
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.28em] text-white/50">Admin Console</div>
        </div>
        <form onSubmit={submit} data-testid="admin-login-form" className="border border-white/10 bg-white p-8">
          <div className="mb-6 flex items-center gap-2 text-brand-ink">
            <Lock className="h-5 w-5 text-brand-blue" />
            <h1 className="font-heading text-xl font-bold">Sign in</h1>
          </div>
          {error && (
            <div data-testid="admin-login-error" className="mb-4 border border-brand-red/30 bg-brand-red/5 px-4 py-3 text-sm text-brand-red">
              {error}
            </div>
          )}
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-brand-muted">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" className="mb-4 h-12 w-full border border-brand-line px-3 text-sm outline-none focus:ring-2 focus:ring-brand-blue" />
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-brand-muted">Password</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" className="mb-6 h-12 w-full border border-brand-line px-3 text-sm outline-none focus:ring-2 focus:ring-brand-blue" />
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand-blue px-6 py-3.5 text-sm font-semibold text-white hover:bg-brand-bluedark disabled:opacity-60">
            {loading ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
